import { isPlatformBrowser } from '@angular/common';
import { Component, inject, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { Router } from '@angular/router';

import { CategorySectionComponent } from '@components/category-section/category-section.component';
import { FeatureCardComponent } from '@components/feature-card/feature-card.component';
import { ProductCardComponent } from '@components/product-card-component/product-card.component';
import { ButtonComponent } from '@components/UI/ButtonComponent/button.component';

import { Category } from '@services/categories/interfaces/categories.interfaces';
import { LandingOverview } from '@services/landing/interfaces/landing.interfaces';
import { LandingService } from '@services/landing/landing.service';
import { Product } from '@services/products/interfaces/product.interfaces';
import { CatalogPricePipe } from '@shared/pipes/catalog-price.pipe';
import { Utils } from '@shared/utils/utils';

import { FeatureItem, FEATURES_ITEMS } from '@constants/components.constants';
import { CDN_CATEGORIES_URL, CDN_PRODUCTS_URL } from '@constants/constants';
import { CATALOG_ROUTE } from '@constants/routes.constants';
import { LANDING_TEXTS } from '@constants/text.constants';

const MAX_FEATURED_PRODUCTS = 3;

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [
    CatalogPricePipe,
    ProductCardComponent,
    FeatureCardComponent,
    CategorySectionComponent,
    ButtonComponent,
  ],
  templateUrl: './landing.html',
  styleUrl: './landing.css',
})
export class Landing implements OnInit {
  public products = signal<Product[]>([]);
  public categories = signal<Category[]>([]);
  public plantProduct = signal<Product | null>(null);
  public isLoading = signal(true);
  public readonly pageTexts = LANDING_TEXTS;
  public readonly features: FeatureItem[] = FEATURES_ITEMS;
  public readonly skeletonItems = Array(MAX_FEATURED_PRODUCTS).fill(null);

  private readonly landingService = inject(LandingService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly utils = inject(Utils);

  ngOnInit() {
    this.utils.scrollToTop(this.platformId);
    if (isPlatformBrowser(this.platformId)) {
      this.setLandingData();
    }
  }

  public navigateToCatalog(): void {
    this.router.navigate([CATALOG_ROUTE]);
  }

  public navigateToEncargo(): void {
    this.router.navigate(['/encargo']);
  }

  // TODO: Improve this method to handle image URLs more robustly, possibly by normalizing data at the service level or using a utility function.
  private setLandingData(): void {
    this.landingService.overview().subscribe((data: LandingOverview) => {
      const products: Product[] = (data?.productsToShow || []).map((product) => {
        if (product && product.imageUrl && !/^https?:\/\//i.test(product.imageUrl)) {
          return { ...product, imageUrl: `${CDN_PRODUCTS_URL}${product.imageUrl}` };
        }
        return product;
      });

      const categories: Category[] = (data?.mainCategories || []).map((category) => {
        if (category && category.imageUrl && !/^https?:\/\//i.test(category.imageUrl)) {
          return { ...category, imageUrl: `${CDN_CATEGORIES_URL}${category.imageUrl}` };
        }
        return category;
      });

      const plantProduct: Product | null = data?.plantProduct ? { ...data.plantProduct } : null;
      if (plantProduct?.imageUrl && !/^https?:\/\//i.test(plantProduct.imageUrl)) {
        plantProduct.imageUrl = `${CDN_PRODUCTS_URL}${plantProduct.imageUrl}`;
      }

      this.products.set(products.slice(0, MAX_FEATURED_PRODUCTS));
      this.categories.set(categories);
      this.plantProduct.set(plantProduct);
      this.isLoading.set(false);
    });
  }
}
